/**
 * g19-equivalent Deposit helpers. Non-throwing. Do not change URL/account rules.
 */
import { PLAYER_PUBLIC_SITE_ID } from '../boot/siteId.js';

export function asArr(v) {
  return Array.isArray(v) ? v : [];
}

/** Display-only. Uses backend DTO `badge`; does not recompute bonus. */
export function visiblePromotionBadge(promo) {
  try {
    const badge = String(promo && promo.badge != null ? promo.badge : '').trim();
    if (!badge) return '';
    const compact = badge.replace(/\s+/g, '').toLowerCase();
    if (
      compact === '0%' ||
      compact === '৳0' ||
      compact === 'tk0' ||
      compact === 'bdt0' ||
      compact === 'n/a' ||
      compact === 'na' ||
      compact === 'nobonus'
    ) {
      return '';
    }
    return badge;
  } catch {
    return '';
  }
}

/** Display-only mask. Never mutates source accountNumber. */
export function maskReceivingNumber(value) {
  try {
    const s = String(value == null ? '' : value);
    if (!s) return '';
    if (s.length >= 5) return `${s.slice(0, 5)}****${s.slice(-2)}`;
    if (s.length <= 2) return '****';
    return `${s.slice(0, 1)}****${s.slice(-1)}`;
  } catch {
    return '****';
  }
}

function defaultApiBase() {
  return `/api/site/${PLAYER_PUBLIC_SITE_ID}/v1`;
}

export function paymentAssetUrl(path, getApiBase) {
  try {
    if (!path) return '';
    const raw = String(path);
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith('/payments/')) return raw;
    let p = raw;
    if (p.startsWith('/api/uploads/')) p = `/uploads/${p.slice('/api/uploads/'.length)}`;
    if (p.startsWith('/uploads/uploads/')) p = `/uploads/${p.slice('/uploads/uploads/'.length)}`;
    if (!p.startsWith('/uploads/')) return raw;
    let api = '';
    try {
      if (typeof getApiBase === 'function') api = String(getApiBase() || '');
      else api = String(defaultApiBase());
    } catch {
      api = '';
    }
    try {
      const abs = new URL(
        api,
        /^https?:\/\//i.test(api)
          ? undefined
          : typeof location !== 'undefined' && location.href
            ? location.href
            : 'http://127.0.0.1/'
      );
      if (/^https?:\/\//i.test(api)) return abs.origin + p;
      return abs.origin + '/api' + p;
    } catch {
      return '/api' + p;
    }
  } catch {
    try {
      return path ? String(path) : '';
    } catch {
      return '';
    }
  }
}

export function flattenDepositAccounts(row) {
  try {
    if (!row || typeof row !== 'object') return [];
    if (Array.isArray(row.accountTypes) && row.accountTypes.length) {
      return row.accountTypes.flatMap((g) => asArr(g && g.accounts));
    }
    return asArr(row.accounts);
  } catch {
    return [];
  }
}

export function depositAccountTypes(row) {
  try {
    if (!row || typeof row !== 'object') return [];
    if (Array.isArray(row.accountTypes) && row.accountTypes.length) {
      return row.accountTypes.filter((g) => g && asArr(g.accounts).length);
    }
    const acc = asArr(row.accounts);
    const map = new Map();
    acc.forEach((a) => {
      if (!a) return;
      const t = a.accountType || 'Personal';
      if (!map.has(t)) map.set(t, []);
      map.get(t).push(a);
    });
    return [...map.entries()].map(([type, accounts]) => ({ type, accounts }));
  } catch {
    return [];
  }
}

const CATALOG = [
  { id: 'bkash', name: 'bKash', logo: '/payments/bkash.svg' },
  { id: 'nagad', name: 'Nagad', logo: '/payments/nagad.svg' },
  { id: 'rocket', name: 'Rocket', logo: '/payments/rocket.svg' },
];

export function projectDepositRender(rows, { selectedId = '', getApiBase } = {}) {
  const managedRows = asArr(rows).filter((row) => row && row.source !== 'config');
  const list = managedRows.map((row) => {
        row = row || {};
        const cat = CATALOG.find((e) => e.id === row.code) || {
          id: row.code,
          name: row.displayName || row.code,
          logo: '',
        };
        const types = depositAccountTypes(row);
        return {
          id: row.code,
          name: row.displayName || cat.name,
          tileName: String(row.displayName || '').trim(),
          logo: paymentAssetUrl(row.icon, getApiBase) || cat.logo,
          managedId: row.id || '',
          accountTypes: types,
          accounts: flattenDepositAccounts(row),
          source: row.source || (row.id ? 'managed' : 'config'),
        };
      });

  const M = list.find((e) => e.id === selectedId) || list[0] || {};
  return { list, M };
}

/** UI-only. Mirrors existing Player amount bounds (min/max) + withdrawable; does not change wallet math. */
export function withdrawSubmitEnabled({
  submitting = false,
  walletReady = false,
  withdrawEnabled = true,
  methodId = '',
  methodsAvailable = 0,
  phoneVerified = false,
  accountNo = '',
  amount,
  minWithdraw,
  maxWithdraw,
  withdrawable,
  turnoverBlocked = false,
  accountDigitLength = 11,
} = {}) {
  if (submitting || !walletReady || withdrawEnabled === false) return false;
  if (!Number(methodsAvailable) || !String(methodId || '').trim()) return false;
  if (!phoneVerified) return false;
  const digits = String(accountNo || '').replace(/\D/g, '');
  if (digits.length !== Number(accountDigitLength)) return false;
  const n = Number(amount);
  const min = Number(minWithdraw);
  const max = Number(maxWithdraw);
  const wd = Number(withdrawable);
  if (!Number.isFinite(n) || n <= 0) return false;
  if (!Number.isFinite(min) || n < min) return false;
  if (!Number.isFinite(max) || n > max) return false;
  if (!Number.isFinite(wd) || n > wd) return false;
  if (turnoverBlocked === true) return false;
  return true;
}

export function isTurnoverWithdrawBlocked(wallet) {
  return wallet?.turnoverBlocked === true;
}

/**
 * UI-only Main Balance. Backend hold keeps `wallet` at the pre-submit total
 * and moves the request into `heldBalance`. Customers must see usable funds
 * (wallet − held), not the ledger total and not a separate Held card.
 * Does not mutate wallet, does not subtract the withdraw amount locally,
 * and does not change accounting / Direct Approve / Gateway Transfer.
 */
export function usableCustomerBalance(wallet = {}) {
  const total = Number(wallet?.wallet ?? wallet?.bdtBalance);
  const held = Number(wallet?.heldBalance);
  const base = Number.isFinite(total) ? total : 0;
  const hold = Number.isFinite(held) && held > 0 ? held : 0;
  return Math.round((base - hold) * 100) / 100;
}

export const TURNOVER_NOTICE_MESSAGE = 'Please complete your turnover.';
export const TURNOVER_NOTICE_CLOSE_LABEL = 'Close turnover notice';

/** Safe popup visibility: never open from render, and never reopen after X on the same mount. */
export function nextTurnoverNoticeState({ turnoverBlocked, dismissed } = {}) {
  if (turnoverBlocked !== true) {
    return { open: false, dismissed: false };
  }
  if (dismissed === true) {
    return { open: false, dismissed: true };
  }
  return { open: true, dismissed: false };
}

export function projectWithdrawRender(rows, { selectedId = '', getApiBase } = {}) {
  const list = asArr(rows)
    .map((row) => {
      const rec = row || {};
      const code = String(rec.code || rec.id || '').trim().toLowerCase();
      if (!code) return null;
      return {
        id: code,
        name: rec.displayName || rec.name || code,
        logo: paymentAssetUrl(rec.icon, getApiBase) || `/payments/${code}.svg`,
        managedId: rec.id || '',
      };
    })
    .filter(Boolean);
  const M = list.find((e) => e.id === selectedId) || list[0] || null;
  return { list, M };
}

export function depositChannelVisible(M) {
  return (M && M.source === 'managed') || asArr(M && M.accountTypes).length;
}

export function depositRecv(M, rid) {
  return asArr(M && M.accounts).find((e) => e && e.id === rid);
}

/** Active receiving accounts for the selected payment-method channel (account type). */
export function accountsForDepositChannel(M, Atp) {
  try {
    const selectedType = asArr(M && M.accountTypes).find((g) => g && g.type === Atp) || {};
    return asArr(selectedType.accounts).filter((a) => a && a.id);
  } catch {
    return [];
  }
}

/** Keep current id when still valid; otherwise first account. Empty list → ''. */
export function resolveInitialReceivingAccountId(accounts, currentId) {
  const list = asArr(accounts).filter((a) => a && a.id);
  if (!list.length) return '';
  const cur = String(currentId || '');
  if (cur && list.some((a) => a.id === cur)) return cur;
  return list[0].id;
}

/** Locked id is immutable even if the eligible list later omits it. */
export function resolveLockedReceivingAccountId(accounts, lockedId, currentId) {
  const want = String(lockedId || '').trim();
  if (want) return want;
  return resolveInitialReceivingAccountId(accounts, currentId);
}

/** Pre-page NMX rotation (step 1); payment-match keeps step-2 rotation. */
export function shouldRotateReceivingNumber({
  depType = 'payment-match',
  depStep,
  locked = false,
  locking = false,
  accountCount = 0,
} = {}) {
  if (locked === true || locking === true) return false;
  if (Number(accountCount) <= 1) return false;
  if (depType === 'nmx') return Number(depStep) === 1;
  return Number(depStep) === 2;
}

export function mergeLockedNmxNumber(numbers, lockedNumber) {
  const row = lockedNumber && typeof lockedNumber === 'object' ? lockedNumber : null;
  const id = String(row?.id || row?.agentNumberId || '').trim();
  if (!id) return asArr(numbers);
  const identifier = String(row.accountIdentifier || row.accountNumber || '').trim();
  const merged = {
    ...row,
    id,
    agentNumberId: id,
    accountIdentifier: identifier,
    accountNumber: identifier || String(row.accountNumber || ''),
  };
  const rest = asArr(numbers).filter(
    (item) => String(item?.id || item?.agentNumberId || '').trim() !== id
  );
  return [merged, ...rest];
}

/** Deterministic next id in list order. Single account stays put. */
export function nextReceivingAccountId(accounts, currentId) {
  const list = asArr(accounts).filter((a) => a && a.id);
  if (!list.length) return '';
  if (list.length === 1) return list[0].id;
  const idx = list.findIndex((a) => a.id === currentId);
  if (idx < 0) return list[0].id;
  return list[(idx + 1) % list.length].id;
}

/** Auto-rotate interval for multi receiving numbers on Deposit step 2. */
export const RECEIVING_NUMBER_ROTATE_MS = 5000;

export function depositQrUrl(Recv, getApiBase) {
  return Recv && Recv.image ? paymentAssetUrl(Recv.image, getApiBase) || '' : '';
}

export function normalizeDepositTrxId(value) {
  return String(value || '').trim();
}

export const DEPOSIT_SUCCESS_POPUP_MS = 3000;
export const WITHDRAW_SUCCESS_POPUP_MS = 1000;
export const WITHDRAW_SUCCESS_POPUP_MESSAGE = 'Withdrawal Request Submitted Successfully!';

/** Fake-timer friendly delay used after a successful POST /withdrawals. */
export function runAfterWithdrawSuccessPopup(onElapsed, {
  setTimeoutFn = globalThis.setTimeout,
  delayMs = WITHDRAW_SUCCESS_POPUP_MS,
} = {}) {
  return setTimeoutFn(onElapsed, delayMs);
}

export function withdrawSuccessUiForApiResult(ok) {
  return {
    showSuccessPopup: ok === true,
    redirectHome: ok === true,
  };
}

export const TRX_CHECK_DEBOUNCE_MS = 400;
export const DEPOSIT_UNAVAILABLE_MESSAGE = 'কাস্টমার প্রতিনিধির সাথে যোগাযোগ করুন।';
export const DEPOSIT_UNAVAILABLE_CLOSE_LABEL = 'Close';

/** Deposit Page quick chips. Visual "+" only — tapping SETs Amount, never adds. */
export const DEPOSIT_QUICK_AMOUNTS = Object.freeze([
  3000, 5000, 10000, 15000, 20000, 25000, 30000, 500,
]);

export function formatDepositQuickAmountLabel(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '+ 0';
  return `+ ${n.toLocaleString('en-US')}`;
}

export function formatDepositLimitRange(min, max) {
  const lo = Number(min);
  const hi = Number(max);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return '';
  const fmt = (n) => `৳ ${Number(n).toLocaleString('en-US')}`;
  return `${fmt(lo)} - ${fmt(hi)}`;
}

/** Quick Amount always replaces the current Amount. It never adds. */
export function applyDepositQuickAmount(_currentAmount, tapped) {
  const n = Number(tapped);
  if (!Number.isFinite(n)) return '';
  return String(n);
}

export function verifiedDepositSenderNumbers(phones) {
  return asArr(phones).filter(
    (row) => row && row.verified === true && String(row.value || '').trim()
  );
}

/** True when the selected Sender Number is any verified profile phone (primary not required). */
export function isVerifiedDepositSenderSelected(phones, selected) {
  const value = String(selected || '').trim();
  if (!value) return false;
  return verifiedDepositSenderNumbers(phones).some((row) => String(row.value || '').trim() === value);
}

export function resolveDepositSenderSelection(phones, currentValue) {
  const rows = verifiedDepositSenderNumbers(phones);
  const cur = String(currentValue || '').trim();
  if (cur && rows.some((row) => String(row.value || '').trim() === cur)) return cur;
  if (rows.length === 1) return String(rows[0].value || '').trim();
  if (rows.length > 1) {
    const primary = rows.find((row) => row.isPrimary === true);
    return String((primary || rows[0]).value || '').trim();
  }
  return '';
}

function playerApiBase() {
  return `/api/site/${PLAYER_PUBLIC_SITE_ID}/v1`;
}

function playerAccessToken() {
  try {
    const raw = localStorage.getItem('ninet6_player_session');
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.accessToken || '';
  } catch {
    return '';
  }
}

function playerAuthHeaders() {
  const token = playerAccessToken();
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function fetchDepositAvailability() {
  try {
    const res = await fetch(`${playerApiBase()}/deposits/availability`, {
      headers: playerAuthHeaders(),
      cache: 'no-store',
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { depositAvailable: false };
    }
    const data = json.data && typeof json.data === 'object' ? json.data : json;
    if (Object.prototype.hasOwnProperty.call(data, 'depositAvailable')) {
      return { depositAvailable: data.depositAvailable === true };
    }
    if (Object.prototype.hasOwnProperty.call(json, 'depositAvailable')) {
      return { depositAvailable: json.depositAvailable === true };
    }
    return { depositAvailable: false };
  } catch {
    return { depositAvailable: false };
  }
}

export async function checkDepositTrxId(trxId) {
  const id = normalizeDepositTrxId(trxId);
  if (!id) return { available: true };
  const params = new URLSearchParams({ trxId: id });
  const res = await fetch(`${playerApiBase()}/deposits/check-trx-id?${params}`, {
    headers: playerAuthHeaders(),
    cache: 'no-store',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.message || json.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return {
    available: json.available !== false,
    message: json.message || '',
  };
}

export const NMX_METHOD_CODE = 'nmx';
export const NMX_UNAVAILABLE_MESSAGE = 'NMX Pay is temporarily unavailable. Please try again later.';

export function isNmxDepositMethod(method) {
  const code = String(method?.id || method?.code || method || '')
    .trim()
    .toLowerCase();
  return code === NMX_METHOD_CODE;
}

export function nmxChannelTypeToProvider(value) {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (!raw) return '';
  if (raw === 'bkash' || raw.includes('bkash')) return 'bkash';
  if (raw === 'nagad' || raw.includes('nagad') || raw === 'nagod' || raw.includes('nagod')) {
    return 'nagad';
  }
  if (raw === 'rocket' || raw.includes('rocket')) return 'rocket';
  return '';
}

/** Resolve Server 2 selectedProvider from the selected Payment Method tile (id, name, logo). */
export function nmxProviderFromPaymentMethod(method) {
  if (method == null) return '';
  if (typeof method !== 'object') return nmxChannelTypeToProvider(method);
  const candidates = [
    method.id,
    method.code,
    method.name,
    method.tileName,
    method.displayName,
    method.provider,
    method.logo,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const provider = nmxChannelTypeToProvider(candidates[i]);
    if (provider) return provider;
  }
  return '';
}

/** UI label for a Server 2 provider that was actually returned. Not a fallback options list. */
export function nmxProviderUiLabel(provider) {
  const code = nmxChannelTypeToProvider(provider);
  if (code === 'bkash') return 'bKash';
  if (code === 'nagad') return 'Nagad';
  if (code === 'rocket') return 'Rocket';
  return '';
}

/** API depositChannel: Agent/Personal → agent/personal. */
export function nmxDepositChannelApiValue(value) {
  const compact = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
  if (compact === 'agent') return 'agent';
  if (compact === 'personal') return 'personal';
  return '';
}

/** Step-1 Deposit Channel: Payment Match keeps managed/types; NMX uses Server 2 payment-options or locked numbers. */
export function depositStep1ChannelVisible({ depType = 'payment-match', M, nmxChannels } = {}) {
  if (depType === 'nmx') {
    if (nmxChannels != null) return asArr(nmxChannels).length > 0;
    return asArr(M && M.accountTypes).length > 0;
  }
  return depositChannelVisible(M);
}

/** Display label from Server 2 accountType. agent→Agent, personal→Personal; other types title-cased. */
export function nmxAccountTypeLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const compact = raw.toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (compact === 'agent') return 'Agent';
  if (compact === 'personal') return 'Personal';
  return raw.replace(/[A-Za-z\u00C0-\u017F]+/g, (word) => {
    if (!word) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}

function nmxCatalogRow(provider) {
  const code = nmxChannelTypeToProvider(provider);
  return CATALOG.find((row) => row.id === code) || null;
}

/**
 * Keep only providers/channels Server 2 returned. Unknown or empty rows are omitted.
 * Does not invent bKash/Nagad/Rocket or Agent/Personal when the API omits them.
 */
export function parseNmxPaymentOptions(json) {
  const root = json && typeof json === 'object' ? json : {};
  const nested = root.data && typeof root.data === 'object' ? root.data : null;
  const list = Array.isArray(root.paymentMethods)
    ? root.paymentMethods
    : Array.isArray(nested && nested.paymentMethods)
      ? nested.paymentMethods
      : [];
  const paymentMethods = [];
  const seenProviders = new Set();
  list.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const provider = nmxChannelTypeToProvider(row.provider);
    if (!provider || seenProviders.has(provider)) return;
    const channels = [];
    const seenChannels = new Set();
    asArr(row.channels).forEach((item) => {
      const channel = nmxDepositChannelApiValue(item);
      if (!channel || seenChannels.has(channel)) return;
      seenChannels.add(channel);
      channels.push(channel);
    });
    if (!channels.length) return;
    seenProviders.add(provider);
    const cat = nmxCatalogRow(provider);
    const apiIcon = String(row.icon || '').trim();
    const resolvedIcon = paymentAssetUrl(apiIcon);
    const apiLogo =
      resolvedIcon.startsWith('/uploads/') ||
      resolvedIcon.startsWith('/api/uploads/') ||
      resolvedIcon.startsWith('/payments/') ||
      /^https?:\/\//i.test(resolvedIcon)
        ? resolvedIcon
        : '';
    paymentMethods.push({
      provider,
      channels,
      name: nmxProviderUiLabel(provider) || (cat && cat.name) || provider,
      logo: apiLogo || (cat && cat.logo) || '',
    });
  });
  return paymentMethods;
}

export function nmxChannelsForProvider(paymentMethods, provider) {
  const code = nmxChannelTypeToProvider(provider);
  const row = asArr(paymentMethods).find((item) => item && item.provider === code);
  return asArr(row && row.channels)
    .map((item) => nmxDepositChannelApiValue(item))
    .filter(Boolean);
}

/**
 * Step-1 Payment Method row. Managed methods stay in their existing order.
 * An NMX provider already listed as a managed method is the same row.
 * An NMX provider with channels that is not managed is appended.
 */
export function depositMethodChoices(matchMethods, nmxPayOptions) {
  const choices = [];
  const seen = new Set();
  asArr(matchMethods).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const id = String(row.id || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    choices.push({
      id,
      name: String(row.name || row.tileName || id).trim() || id,
      tileName: String(row.tileName || '').trim(),
      logo: row.logo || '',
      match: true,
      nmx: nmxChannelsForProvider(nmxPayOptions, id).length > 0,
    });
  });
  asArr(nmxPayOptions).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const id = String(row.provider || '').trim();
    if (!id || seen.has(id)) return;
    if (!nmxChannelsForProvider(nmxPayOptions, id).length) return;
    seen.add(id);
    choices.push({
      id,
      name: String(row.name || id).trim() || id,
      tileName: '',
      logo: row.logo || '',
      match: false,
      nmx: true,
    });
  });
  return choices;
}

/** Payment Match when the method is in the public list. NMX Pay when that provider has channels. */
export function depositTypesForMethod(methodId, matchMethods, nmxPayOptions) {
  const id = String(methodId || '').trim();
  if (!id) return [];
  const types = [];
  if (asArr(matchMethods).some((row) => row && String(row.id || '').trim() === id)) {
    types.push('payment-match');
  }
  if (nmxChannelsForProvider(nmxPayOptions, id).length) types.push('nmx');
  return types;
}

/** Keep the current type when it is still listed. Otherwise take the first listed type. */
export function resolveSelectedDepositType(current, available) {
  const list = asArr(available).filter(Boolean);
  if (!list.length) return String(current || '');
  if (list.includes(current)) return current;
  return list[0];
}

/** Keep the current method when it is still in the union. Otherwise the existing match selection, then the first row. */
export function resolveDepositPayKey(currentKey, choices, fallbackKey) {
  const list = asArr(choices).filter((row) => row && row.id);
  if (!list.length) return '';
  const cur = String(currentKey || '').trim();
  if (cur && list.some((row) => row.id === cur)) return cur;
  const fallback = String(fallbackKey || '').trim();
  if (fallback && list.some((row) => row.id === fallback)) return fallback;
  return list[0].id;
}

/** First API-listed NMX method. Empty list → ''. Does not invent providers. */
export function resolveNmxSelectedProvider(paymentMethods, currentProvider) {
  const methods = asArr(paymentMethods).filter((row) => row && row.provider);
  if (!methods.length) return '';
  const cur = nmxChannelTypeToProvider(currentProvider);
  if (cur && methods.some((row) => row.provider === cur)) return cur;
  return methods[0].provider;
}

/** First API-listed channel for the selected method. Empty/missing → ''. */
export function resolveNmxSelectedChannel(paymentMethods, provider, currentChannel) {
  const channels = nmxChannelsForProvider(paymentMethods, provider);
  if (!channels.length) return '';
  const api = nmxDepositChannelApiValue(currentChannel);
  if (api && channels.includes(api)) return api;
  return channels[0];
}

export function mapNmxEligibleNumberToAccount(number, { channelType } = {}) {
  try {
    const identifier = String(number?.accountIdentifier || number?.accountNumber || '').trim();
    if (!identifier) return null;
    const id = String(number?.id || '').trim();
    if (!id) return null;
    const type = nmxAccountTypeLabel(channelType || number?.accountType || '');
    if (!type) return null;
    return {
      id,
      accountNumber: identifier,
      accountName: String(number?.accountName || ''),
      accountType: type,
      transactionType: String(number?.transactionType || 'Send Money'),
    };
  } catch {
    return null;
  }
}

/** Unique Deposit Channel options from eligible numbers. Types with no identifier are omitted. */
export function groupNmxNumbersByAccountType(numbers) {
  const map = new Map();
  asArr(numbers).forEach((item) => {
    const mapped = mapNmxEligibleNumberToAccount(item);
    if (!mapped || !mapped.accountNumber) return;
    const type = mapped.accountType;
    if (!map.has(type)) map.set(type, []);
    map.get(type).push(mapped);
  });
  return [...map.entries()].map(([type, accounts]) => ({ type, accounts }));
}

/** Existing Payment Method Deposit Channel types for NMX step 1 (labels only; no Recv numbers). */
export function nmxStep1ChannelTypesFromPayMethod(payMethod) {
  const seen = new Set();
  const types = [];
  asArr(payMethod && payMethod.accountTypes).forEach((group) => {
    const type = nmxAccountTypeLabel(group && group.type) || String((group && group.type) || '').trim();
    if (!type || seen.has(type)) return;
    seen.add(type);
    types.push({ type, accounts: [] });
  });
  return types;
}

export function buildNmxDepositView({
  payMethod = {},
  nmxMethod = {},
  numbers = [],
  selectedProvider = '',
} = {}) {
  const types = groupNmxNumbersByAccountType(numbers);
  const pay = payMethod && typeof payMethod === 'object' ? payMethod : {};
  const nmx = nmxMethod && typeof nmxMethod === 'object' ? nmxMethod : {};
  const cat = nmxCatalogRow(selectedProvider);
  return {
    id: NMX_METHOD_CODE,
    name: (cat && cat.name) || nmx.name || pay.name || 'NMX Pay',
    tileName: String((cat && cat.name) || pay.tileName || pay.name || '').trim(),
    logo: (cat && cat.logo) || '',
    managedId: nmx.managedId || '',
    accountTypes: types,
    accounts: types.flatMap((group) => asArr(group.accounts)),
    source: 'managed',
    numberSource: 'nmx-payment-server',
  };
}

export function mergeNmxEligibleNumbersIntoRows(
  rows,
  { methodCode = NMX_METHOD_CODE, numbers = [] } = {}
) {
  const code = String(methodCode || NMX_METHOD_CODE).trim().toLowerCase();
  const types = groupNmxNumbersByAccountType(numbers);
  return asArr(rows).map((row) => {
    if (!row || String(row.code || '').trim().toLowerCase() !== code) return row;
    return {
      ...row,
      accountTypes: types,
      accounts: flattenDepositAccounts({ ...row, accountTypes: types }),
    };
  });
}

export async function fetchNmxPaymentOptions() {
  try {
    const res = await fetch(`${playerApiBase()}/deposits/nmx/payment-options`, {
      method: 'POST',
      headers: {
        ...playerAuthHeaders(),
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      body: JSON.stringify({}),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        paymentMethods: [],
        message: String(json.message || NMX_UNAVAILABLE_MESSAGE),
      };
    }
    return {
      ok: true,
      paymentMethods: parseNmxPaymentOptions(json),
      message: '',
    };
  } catch {
    return { ok: false, paymentMethods: [], message: NMX_UNAVAILABLE_MESSAGE };
  }
}

export async function fetchNmxPaymentSession({
  amount,
  selectedProvider,
  senderNumber,
  depositChannel,
} = {}) {
  const provider =
    selectedProvider && typeof selectedProvider === 'object'
      ? nmxProviderFromPaymentMethod(selectedProvider)
      : nmxChannelTypeToProvider(selectedProvider);
  const channel = nmxDepositChannelApiValue(depositChannel);
  const value = Number(amount);
  const sender = String(senderNumber || '').trim();
  if (!provider || !channel || !Number.isFinite(value) || value <= 0 || !sender) {
    return { ok: false, locked: false, number: null, numbers: [], sessionId: '', message: NMX_UNAVAILABLE_MESSAGE };
  }
  try {
    const res = await fetch(`${playerApiBase()}/deposits/nmx/payment-sessions`, {
      method: 'POST',
      headers: {
        ...playerAuthHeaders(),
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      body: JSON.stringify({
        amount: value,
        selectedProvider: provider,
        senderNumber: sender,
        depositChannel: channel,
      }),
    });
    const json = await res.json().catch(() => ({}));
    const data = json.data && typeof json.data === 'object' ? json.data : json;
    if (!res.ok) {
      return {
        ok: false,
        locked: false,
        number: null,
        numbers: [],
        sessionId: '',
        message: String(json.message || NMX_UNAVAILABLE_MESSAGE),
      };
    }
    const number = data.lockedNumber || data.number || null;
    const confirmedId = String(number?.id || number?.agentNumberId || '').trim();
    const locked = data.locked === true && Boolean(confirmedId);
    if (!locked) {
      return {
        ok: false,
        locked: false,
        number: null,
        numbers: [],
        sessionId: '',
        message: NMX_UNAVAILABLE_MESSAGE,
      };
    }
    const confirmed = { ...number, id: confirmedId, agentNumberId: confirmedId };
    return {
      ok: true,
      locked: true,
      number: confirmed,
      numbers: mergeLockedNmxNumber(data.numbers, confirmed),
      sessionId: String(data.sessionId || '').trim(),
      message: '',
    };
  } catch {
    return { ok: false, locked: false, number: null, numbers: [], sessionId: '', message: NMX_UNAVAILABLE_MESSAGE };
  }
}

/**
 * Best-effort cancel of the exact Server 2 session captured by this Deposit Page instance.
 * Never throws. Never blocks navigation. Duplicate calls for the same sessionId are ignored.
 */
const nmxCancelInFlight = new Set();

export function cancelNmxPaymentSession(sessionId) {
  const id = String(sessionId || '').trim();
  if (!id || nmxCancelInFlight.has(id)) return Promise.resolve();
  nmxCancelInFlight.add(id);
  return fetch(`${playerApiBase()}/deposits/nmx/payment-sessions/cancel`, {
    method: 'POST',
    headers: {
      ...playerAuthHeaders(),
      'Content-Type': 'application/json',
    },
    cache: 'no-store',
    keepalive: true,
    body: JSON.stringify({ sessionId: id }),
  })
    .then(() => undefined)
    .catch(() => undefined);
}

export function simulateDepositRender(rows, { selectedId = '', Atp = '', Rid = '', getApiBase, depType = 'payment-match' } = {}) {
  const { list, M } = projectDepositRender(rows, { selectedId, getApiBase });
  const Recv = depositRecv(M, Rid);
  const showChannel = depositStep1ChannelVisible({ depType, M });
  const typeBtns = asArr(M.accountTypes).map((g) => g && g.type);
  const selectedType = asArr(M.accountTypes).find((g) => g && g.type === Atp) || {};
  const channelAccounts = accountsForDepositChannel(M, Atp);
  const multi = Atp && channelAccounts.length > 1;
  // No manual number picker — multi accounts rotate automatically in the Deposit UI.
  const accBtns = [];
  const qr = depositQrUrl(Recv, getApiBase);
  const txn = Recv ? String(Recv.transactionType || '').toUpperCase() : '';
  const maskedNumber = Recv ? maskReceivingNumber(Recv.accountNumber) : '';
  return { list, M, Recv, showChannel, typeBtns, multi, accBtns, channelAccounts, qr, txn, maskedNumber };
}
